import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { RowSelectionState } from '@tanstack/react-table';
import { Input, Row } from 'antd';
import debounce from 'lodash/debounce';
import { FileUploadHint, FILE_SIZE_LIMIT_IN_MEGABYTES } from '@/entities/file';
import { useNotificationContext } from '@/features/notifications';
import {
  useListGoogleDriveFiles,
  useListGoogleDrives,
  useUploadGoogleDriveFilesToOpenAI,
} from '@/shared/api';
import { useTokenBasedPaging } from '@/shared/hooks';
import { useRouterContext } from '@/shared/router';
import {
  FileExplorerActions,
  FileExplorerBreadCrumb,
  FileExplorerTable,
} from '@/shared/ui';
import { captureError } from '@/shared/utils';

interface GDriveFileExplorerProps {
  onFilesAttach: () => void;
  isConnected: boolean;
}

const pageSize = 10;
const orderBy = 'createdTime desc';
const initialFolder = {
  id: 'drives',
  name: 'The List of Drives',
};

export const GDriveFileExplorer: React.FC<GDriveFileExplorerProps> = ({
  onFilesAttach,
  isConnected,
}) => {
  const { user } = useAuth0();
  const { showNotification } = useNotificationContext();
  const { activeOrganization } = useRouterContext();
  const { currentPageToken, onPrevPage, onNextPage, onFirstPage } =
    useTokenBasedPaging();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedFileIds = Object.keys(rowSelection);
  const [searchTerm, setSearchTerm] = useState('');
  const [folderStack, setFolderStack] = useState([initialFolder]);
  const [, currentDrive] = folderStack;
  const currentFolder = folderStack[folderStack.length - 1];

  const { data: drivesData, isFetching: loadingDrives } = useListGoogleDrives(
    { queryParams: { serviceType: 'google' } },
    { enabled: isConnected && !!user?.sub },
  );

  const { data: filesData, isFetching: loadingFiles } = useListGoogleDriveFiles(
    {
      queryParams: {
        serviceType: 'google',
        userId: user?.sub || '',
        folderId: currentFolder?.id,
        driveId: currentDrive?.id,
        pageToken: currentPageToken,
        pageSize,
        orderBy,
        searchTerm,
      },
    },
    { enabled: isConnected && !!user?.sub && !!currentDrive?.id },
  );

  const { mutateAsync: uploadFiles, isLoading: uploading } =
    useUploadGoogleDriveFilesToOpenAI({
      onSuccess: async () => {
        onFilesAttach();
        showNotification({
          type: 'success',
          message: 'Success',
          description: 'Files attached successfully',
        });
      },
      onError: (error) => {
        captureError(error, {
          module: 'knowledge_base',
          action: 'attach_files',
          metadata: { organizationId: activeOrganization?.id },
        });
        showNotification({
          type: 'error',
          message: 'Error attaching files',
          description: error?.message || 'Failed to attach files',
        });
      },
    });

  const isLoading = loadingDrives || loadingFiles || uploading;
  const displayData = useMemo(
    () => (currentDrive ? filesData?.files : drivesData?.drives),
    [currentDrive, drivesData, filesData],
  );

  useEffect(() => {
    setSearchTerm('');
    onFirstPage();
  }, [folderStack, onFirstPage]);

  return (
    <>
      <FileUploadHint
        maxFileSizeInMb={FILE_SIZE_LIMIT_IN_MEGABYTES}
        style={{ color: 'gray' }}
      />
      <FileExplorerBreadCrumb
        items={folderStack}
        loading={isLoading}
        onSelect={(_, __, nextFolders) => setFolderStack(nextFolders)}
      />
      {currentDrive && (
        <Row style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search files..."
            onChange={debounce(
              ({ target }) => setSearchTerm(target.value),
              500,
            )}
            allowClear
          />
        </Row>
      )}
      <FileExplorerTable
        data={displayData}
        loading={isLoading}
        rowSelection={rowSelection}
        onRowSelect={setRowSelection}
        onRowClick={(row) =>
          !row.isFolder && row.webViewLink
            ? window.open(row.webViewLink, '_blank')
            : setFolderStack([...folderStack, row])
        }
        enableRowSelection={({ original }) => !original.isFolder}
        isRowClickable={() => true}
        showRowsSelection
      />
      <FileExplorerActions
        loading={isLoading}
        showNavActions={!!currentDrive}
        canNavNextPage={!!filesData?.nextPageToken}
        canNavPrevPage={!!currentPageToken}
        onPrevPage={onPrevPage}
        onNextPage={() => onNextPage(filesData?.nextPageToken)}
        canSubmit={!!selectedFileIds.length}
        onSubmit={() => uploadFiles({ body: { fileIds: selectedFileIds } })}
      />
    </>
  );
};
